import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import "./EditCohortModel.scss";
import { Modal } from "react-bootstrap";
import exportimages from "../../../../assets/images/exportImages";
import { useDispatch } from "react-redux";
import {
  editCohortSlice,
  setCohortApplied,
  setMode,
  setSelectedCohort,
} from "../../../../rtk-query/features/cohort/cohortSlice";
import { usePermissions } from "../../../../hooks/usePermissions";
import { useSubscription } from "../../../../hooks/useSubscription";
import { PRIVILEGES } from "../../../../utils/privileges";
import { COHORT_MODE } from "../CohortList/CohortList";

/* -------------------- CONSTANTS -------------------- */
export const EDIT_MODEL = {
  EDIT: "edit",
  SUCCESS: "success",
};

const FIELD_NAME = {
  TITLE: "cohortTitle",
  DESCRIPTION: "cohortDesc",
};

const INITIAL_FORM = {
  [FIELD_NAME.TITLE]: "",
  [FIELD_NAME.DESCRIPTION]: "",
};

const INITIAL_ERRORS = {
  [FIELD_NAME.TITLE]: "",
  [FIELD_NAME.DESCRIPTION]: "",
};

const INPUT_ID = {
  TITLE: "edit-cohort-title",
  DESCRIPTION: "edit-cohort-description",
};

/* -------------------- HELPERS -------------------- */
/* istanbul ignore next */
const containsEmoji = (value = "") => /\p{Extended_Pictographic}/u.test(value);

const validateField = (name, value = "") => {
  if (name === FIELD_NAME.TITLE) {
    if (!value) return "Cohort name is required";
    if (containsEmoji(value)) return "Emojis are not allowed";
  }

  if (name === FIELD_NAME.DESCRIPTION && containsEmoji(value)) {
    return "Emojis are not allowed";
  }

  return "";
};

/* -------------------- FORM COMPONENT -------------------- */
const EditCohortForm = ({
  form,
  errors,
  onChange,
  onFieldKeyDown,
  onSubmit,
  onCancel,
  isSubmitting,
  isAllowed,
}) => (
  <div className="edit-cohort-modal">
    <div className="modal-header">
      <div className="icon-outer-circle">
        <div className="icon-inner-circle">
          <img alt="icon" src={exportimages.edit_cohort_icon} />
        </div>
      </div>
    </div>

    <h2 className="mb-10">Edit Cohort Details</h2>

    <div className="form-group">
      <label htmlFor={INPUT_ID.TITLE}>Cohort Name</label>
      <input
        id={INPUT_ID.TITLE}
        type="text"
        name={FIELD_NAME.TITLE}
        value={form.cohortTitle}
        placeholder="Enter cohort name"
        onChange={onChange}
        onKeyDown={onFieldKeyDown}
      />
      {errors.cohortTitle && (
        <span className="error-text">{errors.cohortTitle}</span>
      )}
    </div>

    <div className="form-group">
      <label htmlFor={INPUT_ID.DESCRIPTION}>Description</label>
      <textarea
        id={INPUT_ID.DESCRIPTION}
        name={FIELD_NAME.DESCRIPTION}
        value={form.cohortDesc}
        placeholder="Enter cohort description"
        onChange={onChange}
        onKeyDown={onFieldKeyDown}
      />
      {errors.cohortDesc && (
        <span className="error-text">{errors.cohortDesc}</span>
      )}
    </div>

    <div className="actions">
      <button type="button" className="btn cancel" onClick={onCancel}>
        Cancel
      </button>

      <button
        type="button"
        className="btn primary"
        onClick={onSubmit}
        disabled={!isAllowed || isSubmitting}
      >
        {isSubmitting ? "Saving..." : "Save Edits"}
      </button>
    </div>
  </div>
);

EditCohortForm.propTypes = {
  form: PropTypes.shape({
    cohortTitle: PropTypes.string.isRequired,
    cohortDesc: PropTypes.string.isRequired,
  }).isRequired,
  errors: PropTypes.shape({
    cohortTitle: PropTypes.string.isRequired,
    cohortDesc: PropTypes.string.isRequired,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onFieldKeyDown: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  isAllowed: PropTypes.bool.isRequired,
};

/* -------------------- SUCCESS COMPONENT -------------------- */
const EditCohortSuccess = ({ title, onClose }) => (
  <div className="success-cohort-modal">
    <div className="modal-header">
      <div className="icon-outer-circle">
        <div className="icon-inner-circle">
          <img alt="icon" src={exportimages.cohort_success_icon} />
        </div>
      </div>
    </div>

    <h2>Cohort Saved Successfully</h2>
    <p className="subtitle">
      Cohort <strong>{title}</strong> was saved successfully.
    </p>

    <div className="actions">
      <button type="button" className="btn cancel" onClick={onClose}>
        Close
      </button>
    </div>
  </div>
);

EditCohortSuccess.propTypes = {
  title: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

/* -------------------- MAIN COMPONENT -------------------- */
export const EditCohortModel = ({
  uid,
  title = "",
  subtitle = "",
  switchEditModel,
  setSwitchEditModel,
  onHide,
  ...rest
}) => {
  const dispatch = useDispatch();
  const { hasPermission } = usePermissions();
  const { hasSubscription } = useSubscription();

  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState(INITIAL_ERRORS);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const cohortTitle = form[FIELD_NAME.TITLE];
  /* -------------------- INIT FROM PROPS -------------------- */
  useEffect(() => {
    if (switchEditModel !== EDIT_MODEL.EDIT) {
      return;
    }

    setForm({
      cohortTitle: title,
      cohortDesc: subtitle,
    });
    setErrors(INITIAL_ERRORS);
  }, [switchEditModel, title, subtitle]);

  /* -------------------- HANDLERS -------------------- */
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;

    setForm((prev) => ({ ...prev, [name]: value }));

    const errorMsg = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: errorMsg }));
  }, []);

  const handleFieldKeyDown = useCallback((e) => {
    if (e.key === " ") {
      e.stopPropagation();
    }
  }, []);

  const isFormValid = Boolean(cohortTitle) && !errors[FIELD_NAME.TITLE];

  const handleEdit = useCallback(async () => {
    /* istanbul ignore next */
    if (!isFormValid) {
      return;
    }

    try {
      setIsSubmitting(true);

      const data = await dispatch(editCohortSlice({ ...form, uid })).unwrap();

      setSwitchEditModel(EDIT_MODEL.SUCCESS);
      dispatch(setMode(COHORT_MODE.CUSTOM));
      dispatch(setCohortApplied(null));
      dispatch(setSelectedCohort(data?.data));
    } catch (err) {
      console.error("Edit cohort failed", err);
    } finally {
      setIsSubmitting(false);
    }
  }, [dispatch, form, isFormValid, setSwitchEditModel, uid]);

  const isAllowed =
    hasPermission(PRIVILEGES.CREATE_COHORT) &&
    hasSubscription(PRIVILEGES.CREATE_COHORT) &&
    isFormValid;

  /* -------------------- UI -------------------- */
  return (
    <Modal {...rest} onHide={onHide} centered>
      <Modal.Body className="cohort-modal">
        {switchEditModel === EDIT_MODEL.EDIT ? (
          <EditCohortForm
            form={form}
            errors={errors}
            onChange={handleChange}
            onSubmit={handleEdit}
            onCancel={onHide}
            isSubmitting={isSubmitting}
            isAllowed={isAllowed}
            onFieldKeyDown={handleFieldKeyDown}
          />
        ) : (
          <EditCohortSuccess title={cohortTitle} onClose={onHide} />
        )}
      </Modal.Body>
    </Modal>
  );
};

EditCohortModel.propTypes = {
  uid: PropTypes.string.isRequired,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  switchEditModel: PropTypes.string.isRequired,
  setSwitchEditModel: PropTypes.func.isRequired,
  onHide: PropTypes.func.isRequired,
};
